import { forwardRef, type HTMLAttributes, type ReactNode } from 'react'
import { cn } from '../helpers/classnames'
import { X } from 'lucide-react'

export interface TagProps extends HTMLAttributes<HTMLSpanElement> {
  size?: 'sm' | 'md' | 'lg'
  variant?: 'solid' | 'subtle' | 'outline'
  colorScheme?: 'gray' | 'red' | 'green' | 'blue' | 'yellow' | 'purple' | 'pink' | 'brand'
  children: ReactNode
}

export const Tag = forwardRef<HTMLSpanElement, TagProps>(
  ({ children, size = 'md', variant = 'subtle', colorScheme = 'gray', className, ...rest }, ref) => {
    const sizeClasses = {
      sm: 'px-2 py-0.5 text-xs',
      md: 'px-2.5 py-1 text-sm',
      lg: 'px-3 py-1.5 text-base'
    }

    const variantClasses = {
      solid: {
        gray: 'bg-gray-500 text-white',
        red: 'bg-red-500 text-white',
        green: 'bg-green-500 text-white',
        blue: 'bg-blue-500 text-white',
        yellow: 'bg-yellow-500 text-white',
        purple: 'bg-purple-500 text-white',
        pink: 'bg-pink-500 text-white',
        brand: 'bg-stampede-500 text-white'
      },
      subtle: {
        gray: 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200',
        red: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-200',
        green: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-200',
        blue: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-200',
        yellow: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-200',
        purple: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-200',
        pink: 'bg-pink-100 text-pink-800 dark:bg-pink-900/30 dark:text-pink-200',
        brand: 'bg-stampede-100 text-stampede-800 dark:bg-stampede-900/30 dark:text-stampede-200'
      },
      outline: {
        gray: 'border border-gray-300 text-gray-700 dark:border-gray-600 dark:text-gray-300',
        red: 'border border-red-300 text-red-700 dark:border-red-600 dark:text-red-300',
        green: 'border border-green-300 text-green-700 dark:border-green-600 dark:text-green-300',
        blue: 'border border-blue-300 text-blue-700 dark:border-blue-600 dark:text-blue-300',
        yellow: 'border border-yellow-300 text-yellow-700 dark:border-yellow-600 dark:text-yellow-300',
        purple: 'border border-purple-300 text-purple-700 dark:border-purple-600 dark:text-purple-300',
        pink: 'border border-pink-300 text-pink-700 dark:border-pink-600 dark:text-pink-300',
        brand: 'border border-stampede-300 text-stampede-700 dark:border-stampede-600 dark:text-stampede-300'
      }
    }

    return (
      <span
        ref={ref}
        className={cn(
          'inline-flex items-center rounded-md font-medium',
          sizeClasses[size],
          variantClasses[variant][colorScheme],
          className
        )}
        {...rest}
      >
        {children}
      </span>
    )
  }
)

Tag.displayName = 'Tag'

export interface TagLabelProps extends HTMLAttributes<HTMLSpanElement> {}

export const TagLabel = forwardRef<HTMLSpanElement, TagLabelProps>(
  ({ children, className, ...rest }, ref) => {
    return (
      <span ref={ref} className={cn('', className)} {...rest}>
        {children}
      </span>
    )
  }
)

TagLabel.displayName = 'TagLabel'

export interface TagCloseButtonProps extends HTMLAttributes<HTMLButtonElement> {
  onClick?: () => void
}

export const TagCloseButton = forwardRef<HTMLButtonElement, TagCloseButtonProps>(
  ({ onClick, className, ...rest }, ref) => {
    return (
      <button
        ref={ref}
        type="button"
        onClick={onClick}
        className={cn(
          'ml-1 inline-flex h-4 w-4 items-center justify-center rounded hover:bg-black/10 dark:hover:bg-white/10',
          className
        )}
        {...rest}
      >
        <X className="h-3 w-3" />
      </button>
    )
  }
)

TagCloseButton.displayName = 'TagCloseButton'

export default Tag